// src/components/tiptap/extensions/AudioRecorderNode.jsx
// AudioRecorderNode.jsx (drop-in)
// - Cross-browser MediaRecorder MIME negotiation (AAC/MP4 for iPad Safari)
// - Correct file extensions (webm/ogg/m4a/mp3) and contentType
// - Resumes suspended AudioContext on iOS to render waveform

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../config/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon.jsx';

const { FiMic, FiTrash2, FiPlay, FiSquare, FiUploadCloud } = FiIcons;

const AUTO_DELETE_ON_UNMOUNT = false;

// Per-page-load cache to avoid re-signing the same audio repeatedly.
// Key: `id:<uuid>` (preferred) or `key:<storageKey>`
const signedUrlCache = new Map();
// In-flight dedupe so multiple mounts don't hammer the Edge Function
const signedUrlInflight = new Map();

export const AudioRecorderNode = Node.create({
  name: 'audioRecorder',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      id: { default: null },                // audio_recordings.id
      noteBlockId: { default: null },       // UUID to tie node↔row
      fileUrl: { default: null },           // storage key for private bucket
      durationMs: { default: null },
      status: { default: 'idle' },          // idle | recording | recorded | uploading | saved | error
      title: { default: 'Audio Recording' },
      lessonNoteId: { default: null },      // required
      studentId: { default: null },         // optional
      studentMasterId: { default: null },   // optional
      mimeType: { default: null },
    };
  },

  parseHTML() { return [{ tag: 'audio-recorder' }]; },
  renderHTML({ HTMLAttributes }) { return ['audio-recorder', mergeAttributes(HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(AudioRecorderView); },

  addKeyboardShortcuts() {
    if (AUTO_DELETE_ON_UNMOUNT) return {};
    const shouldBlock = ({ editor }) => {
     const sel = editor.state.selection;
     // Only block when a node selection is on an audioRecorder node
     return !!sel?.node && sel.node.type?.name === 'audioRecorder';
   };
   return {
     Backspace: ({ editor }) => (shouldBlock({ editor }) ? true : false),
     Delete:    ({ editor }) => (shouldBlock({ editor }) ? true : false),
   };
  },
});

function AudioRecorderView({ node, updateAttributes, editor, getPos }) {
  const { id, noteBlockId, lessonNoteId, status, title, fileUrl, mimeType } = node.attrs;

  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [signedUrl, setSignedUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [canDelete, setCanDelete] = useState(false); // parents should never see delete

  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // waveform
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);

  const skipUnmountCleanupRef = useRef(false);

  // Determine whether the current viewer is allowed to delete recordings.
  // In MMS: teachers resolve current_user_id(); parents do not.
   useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('current_user_id');
        if (cancelled) return;
        setCanDelete(!error && !!data);
      } catch {
        if (!cancelled) setCanDelete(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ensure attrs present (hydrate from editor.storage)
  useEffect(() => {
    const patch = {};
    if (!noteBlockId) patch.noteBlockId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
    const storage = editor && editor.storage && editor.storage.mms ? editor.storage.mms : null;
    if (!node.attrs.lessonNoteId && storage && storage.lessonNoteId) patch.lessonNoteId = storage.lessonNoteId;
    if (!node.attrs.studentId && storage && storage.studentId) patch.studentId = storage.studentId;
    if (!node.attrs.studentMasterId && storage && storage.studentMasterId) patch.studentMasterId = storage.studentMasterId;
    if (Object.keys(patch).length) updateAttributes(patch);
  }, [editor, noteBlockId, node.attrs.lessonNoteId, node.attrs.studentId, node.attrs.studentMasterId, updateAttributes]);

  // create signed URL for private playback
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // leave existing signedUrl while revalidating
      if (!id && !fileUrl) return;

      if (fileUrl && /^https?:\/\//i.test(fileUrl)) {
        if (!cancelled) setSignedUrl(fileUrl);
        return;
      }

      const cacheKey = id ? `id:${id}` : `key:${fileUrl}`;
      const cached = signedUrlCache.get(cacheKey);
      if (cached && cached.url && cached.expiresAt > Date.now() + 30_000) {
        if (!cancelled) setSignedUrl(cached.url);
        return;
      }

      try {
        // Parent-safe signing: server signs with service role after RLS check
            const key = fileUrl
          ? String(fileUrl).replace(/^\/+/, '').replace(/^user-audio\//, '')
          : null;

       let p = signedUrlInflight.get(cacheKey);
        if (!p) {
          p = supabase.functions.invoke('sign-user-audio', {
            body: {
              recordingId: id || undefined,
              key: id ? undefined : key || undefined,
              expiresIn: 60 * 60 * 24 * 7,
            },
          });
          signedUrlInflight.set(cacheKey, p);
        }
        let res;
        try {
        res = await p;
        } finally {
        signedUrlInflight.delete(cacheKey);
        }

        if (res.error) throw res.error;
        const signed = res.data?.signedUrl || null;
        if (!cancelled) setSignedUrl(signed);

        if (signed) {
          // expiresIn is 7 days; cache for slightly less to be safe
          signedUrlCache.set(cacheKey, {
            url: signed,
            expiresAt: Date.now() + (60 * 60 * 24 * 7 - 60) * 1000, // 7d minus 60s buffer
          });
        }
      } catch (e) {
        console.error('sign-user-audio failed:', e);
        if (!cancelled) setSignedUrl(null);
        signedUrlInflight.delete(cacheKey);
      }
    })();
    return () => { cancelled = true; };
  }, [id, fileUrl]);

  // unmount cleanup (keyboard deletion, cut, etc.)
  useEffect(() => {
    return () => {
      stopAllRecordingInfra();
      if (!AUTO_DELETE_ON_UNMOUNT) return;
      if (skipUnmountCleanupRef.current) return;
      if (!node.attrs.id && !node.attrs.fileUrl) return;

      (async () => {
        try {
          await deleteRecordingRemote({
            id: node.attrs.id,
            lessonNoteId: node.attrs.lessonNoteId,
            noteBlockId: node.attrs.noteBlockId,
            fileUrl: node.attrs.fileUrl,
          });
        } catch (e) {
          console.warn('Unmount cleanup failed:', e);
        }
      })();
    };
  }, [node.attrs.fileUrl, node.attrs.id, node.attrs.lessonNoteId, node.attrs.noteBlockId]);

  function startTimer() {
    stopTimer();
    const t0 = Date.now();
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - t0), 200);
  }
  function stopTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function stopAllRecordingInfra() {
    try {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
    } catch (e) {
      console.warn('mediaRecorder.stop failed', e);
    }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
      streamRef.current = null;
    }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (sourceRef.current) sourceRef.current.disconnect();
    if (analyserRef.current) analyserRef.current.disconnect();
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { /* ignore */ }
      audioCtxRef.current = null;
    }
    stopTimer();
  }

  async function start() {
    try {
      updateAttributes({ status: 'recording' });
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Cross-browser MIME negotiation; iPad/Safari prefers AAC in MP4
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/mpeg', // mp3 fallback in some Chromium builds
      ];
      let mt = null;
      if (window.MediaRecorder && typeof MediaRecorder.isTypeSupported === 'function') {
        mt = candidates.find(m => {
          try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
        }) || null;
      }
      const mr = mt ? new MediaRecorder(stream, { mimeType: mt }) : new MediaRecorder(stream);
      const local = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) local.push(e.data); };
      mr.onstop = () => { setChunks(local); updateAttributes({ status: 'recorded' }); };
      mr.start(250);
      setMediaRecorder(mr);
      updateAttributes({ mimeType: mt || null });
      startTimer();
      setupWaveform(stream, canvasRef, audioCtxRef, analyserRef, sourceRef, rafRef);
    } catch (e) {
      console.error('start() failed:', e);
      stopAllRecordingInfra();
      updateAttributes({ status: 'error' });
    }
  }

  function stop() {
    try {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
      stopAllRecordingInfra();
      if (chunks.length) updateAttributes({ status: 'recorded' });
    } catch (e) {
      console.error('stop() failed:', e);
      updateAttributes({ status: 'error' });
    }
  }

  async function save() {
    try {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        await new Promise((res) => {
          mediaRecorder.addEventListener('stop', res, { once: true });
          mediaRecorder.stop();
        });
      }
      stopAllRecordingInfra();

      if (!chunks.length) throw new Error('No audio data');
      if (!lessonNoteId) throw new Error('Missing lessonNoteId for audio recorder');

      let nbid = noteBlockId;
      if (!nbid) {
        nbid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
        updateAttributes({ noteBlockId: nbid });
      }

      updateAttributes({ status: 'uploading' });

      const chosenType = mimeType || 'audio/mp4'; // OK for Safari; others will still accept Blob
      const blob = new Blob(chunks, { type: chosenType });
      const durationMs = await getDurationMs(blob);

      // Resolve canonical users.id from the DB (bypasses users RLS ambiguity)
      const { data: userId, error: uidErr } = await supabase.rpc('current_user_id');
      if (uidErr || !userId) throw new Error('User not resolved (current_user_id)');

      const ext = extFromMime(chosenType);
      const storageKey = `${userId}/${lessonNoteId}/${nbid}.${ext}`;
      const up = await supabase.storage.from('user-audio').upload(storageKey, blob, {
        contentType: chosenType,
        upsert: true,
      });
      if (up.error) throw up.error;

      const rpc = await supabase.rpc('upsert_audio_recording', {
        p_lesson_note_id: lessonNoteId,
        p_note_block_id: nbid,
        p_title: title,
        p_file_url: storageKey,       // store the STORAGE KEY for private bucket
        p_duration_ms: durationMs,
        p_created_by: userId,
        p_student_id: node.attrs.studentId || null,
        p_student_master_id: node.attrs.studentMasterId || null,
      });
      if (rpc.error) throw rpc.error;
      const row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
      if (!row?.id) throw new Error('RPC did not return a row');

      // Always sign via Edge Function (parent-safe path, consistent behavior)
      const s = await supabase.functions.invoke('sign-user-audio', {
        body: { recordingId: row.id, expiresIn: 60 * 60 * 24 * 7 },
      });
      if (s.error) throw s.error;

      updateAttributes({
        id: row.id,
        fileUrl: row.file_url,
        durationMs,
        status: 'saved',
      });
      setSignedUrl(s.data?.signedUrl ?? null);
      setChunks([]);
    } catch (e) {
      console.error(e);
      updateAttributes({ status: 'error' });
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this recording? This removes the file and database row.')) return;
    setDeleting(true);
    try {
      stopAllRecordingInfra();
      await deleteRecordingRemote({
        id: node.attrs.id,
        lessonNoteId: node.attrs.lessonNoteId,
        noteBlockId: node.attrs.noteBlockId,
        fileUrl: node.attrs.fileUrl,
      });
      skipUnmountCleanupRef.current = true;
      const pos = typeof getPos === 'function' ? getPos() : null;
      if (typeof pos === 'number') {
        editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + node.nodeSize })
          .insertContent({ type: 'paragraph' })
          .run();
      } else {
        updateAttributes({ id: null, fileUrl: null, durationMs: null, status: 'idle' });
      }
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Sorry—could not delete this recording.');
    } finally {
      setDeleting(false);
    }
  }

  const isRecording = status === 'recording';
  const isRecorded = status === 'recorded' || (chunks.length > 0 && status === 'idle');
  const isUploading = status === 'uploading';
  const isSaved = status === 'saved';
  const isErrored = status === 'error';
  const formattedElapsed = formatMs(elapsedMs);

  return (
    <NodeViewWrapper className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiMic} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">{title}</div>
            <div className="text-sm text-slate-500">Record, save, and play back audio.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(id || fileUrl) && canDelete && (
            <button
              className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
              onClick={handleDelete}
              disabled={deleting || isUploading}
              title="Delete recording"
            >
              {deleting ? <span className="text-xs">...</span> : <SafeIcon icon={FiTrash2} className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {signedUrl ? (
        <audio controls src={signedUrl} className="w-full" />
      ) : (
        <>
          {isRecording && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <span className="text-sm font-medium text-red-700">Recording…</span>
                <span className="text-sm text-slate-500">{formattedElapsed}</span>
              </div>
              <div className="rounded-lg border border-slate-200 p-2 bg-slate-100/80">
                <canvas
                  ref={canvasRef}
                  style={{ width: '100%', height: 28, display: 'block' }}
                  width={600}
                  height={28}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={start}
              disabled={isRecording || isUploading}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 ${
                isRecording ? 'bg-slate-200 text-slate-500 border-slate-200 cursor-default' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
              }`}
              title="Start recording"
            >
              <SafeIcon icon={FiPlay} />
              Record
            </button>
            <button
              onClick={stop}
              disabled={!isRecording || isUploading}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 ${
                isRecording ? 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100' : 'bg-slate-200 text-slate-500 border-slate-200 cursor-default'
              }`}
              title="Stop recording"
            >
              <SafeIcon icon={FiSquare} />
              Stop
            </button>
            <button
              onClick={save}
              disabled={!(isRecorded || (chunks.length > 0 && !isUploading)) || isUploading}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold text-white shadow-sm transition-all flex items-center gap-2 ${
                isUploading
                  ? 'bg-slate-400 cursor-wait'
                  : isRecorded || chunks.length > 0
                  ? 'bg-orange-500 hover:bg-orange-600 active:scale-95'
                  : 'bg-slate-400 cursor-not-allowed'
              }`}
              title={isRecorded || chunks.length > 0 ? 'Save this recording' : 'Record something to enable Save'}
            >
              <SafeIcon icon={FiUploadCloud} />
              {isUploading ? 'Saving…' : 'Save'}
            </button>
          </div>

          {!isRecording && (
            <p className="text-xs text-slate-500 mt-2">
              Tip: Click <span className="font-semibold">Record</span>, then <span className="font-semibold">Stop</span>, then <span className="font-semibold">Save</span>.
            </p>
          )}
          {isErrored && <p className="text-xs text-red-600 mt-2">Something went wrong. Please try again.</p>}
        </>
      )}
    </NodeViewWrapper>
  );
}

/* helpers (pure JS) */
function formatMs(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function getDurationMs(blob) {
  return new Promise((resolve) => {
    const el = document.createElement('audio');
    const url = URL.createObjectURL(blob);
    el.src = url;
    el.addEventListener('loadedmetadata', () => {
      const seconds = Number.isFinite(el.duration) ? el.duration : 0;
      URL.revokeObjectURL(url);
      resolve(Math.round((seconds || 0) * 1000));
    });
  });
}

async function deleteRecordingRemote({ id, lessonNoteId, noteBlockId, fileUrl }) {
  const storageKey = await resolveStorageKey(fileUrl, lessonNoteId, noteBlockId);

  let rowErr = null;
  if (id) {
    const del = await supabase.from('audio_recordings').delete().eq('id', id);
    rowErr = del.error || null;
  } else if (lessonNoteId && noteBlockId) {
    const del2 = await supabase
      .from('audio_recordings')
      .delete()
      .eq('lesson_note_id', lessonNoteId)
      .eq('note_block_id', noteBlockId);
    rowErr = del2.error || null;
  }

  if (storageKey) {
    const rm = await supabase.storage.from('user-audio').remove([storageKey]);
    if (rm.error) console.warn('Storage remove warning:', rm.error);
  }

  if (rowErr) throw rowErr;
}

async function resolveStorageKey(fileUrl, lessonNoteId, noteBlockId) {
  if (!fileUrl) return null;
  if (!/^https?:\/\//i.test(fileUrl)) return fileUrl;

  try {
    const u = new URL(fileUrl);
    // handle both public and signed URL shapes
    const candidates = [
      '/storage/v1/object/public/user-audio/',
      '/storage/v1/object/sign/user-audio/',
    ];
    for (const marker of candidates) {
      const idx = u.pathname.indexOf(marker);
      if (idx >= 0) {
        // for signed URLs, everything after marker is "<key>?token=…"; strip query
        const pathPart = u.pathname.substring(idx + marker.length);
        return pathPart.replace(/^\//, '');
      }
    }
  } catch { /* ignore */ }

  // best-effort fallback if we only have IDs
  try {
    const userRow = await supabase.from('users').select('id').limit(1).maybeSingle();
    if (!userRow.error && userRow.data?.id && lessonNoteId && noteBlockId) {
      return `${userRow.data.id}/${lessonNoteId}/${noteBlockId}.webm`;
    }
  } catch { /* ignore */ }
  return null;
}

function setupWaveform(stream, canvasRef, audioCtxRef, analyserRef, sourceRef, rafRef) {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioContextCtor();
  // On iOS the context may start suspended. Resume on user-initiated call.
  if (audioCtx.state === 'suspended' && typeof audioCtx.resume === 'function') {
    audioCtx.resume().catch(() => {});
  }
  const source = audioCtx.createMediaStreamSource(stream);
  const analyser = audioCtx.createAnalyser();

  analyser.fftSize = 2048;
  const bufferLength = analyser.fftSize;
  const dataArray = new Uint8Array(bufferLength);

  source.connect(analyser);

  audioCtxRef.current = audioCtx;
  analyserRef.current = analyser;
  sourceRef.current = source;

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 600;
  const cssH = canvas.clientHeight || 28;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);

  function draw() {
    rafRef.current = requestAnimationFrame(draw);
    analyser.getByteTimeDomainData(dataArray);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 1.5 * dpr;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(249, 115, 22, 0.9)'; // Orange color for waveform

    ctx.beginPath();
    const sliceWidth = canvas.width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = (v * canvas.height) / 2;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      x += sliceWidth;
    }
    ctx.stroke();
  }
  draw();
}

function extFromMime(mt) {
  if (!mt) return 'm4a'; // Safari will produce AAC even if we didn't pass a mime
  const m = mt.toLowerCase();
  if (m.includes('ogg')) return 'ogg';
  if (m.includes('webm')) return 'webm';
  if (m.includes('mpeg')) return 'mp3';
  if (m.includes('mp4') || m.includes('aac')) return 'm4a';
  return 'm4a';
}

export { AudioRecorderNode as default };